import { Component, computed, input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

/**
 * R4 refonte DDM/DLC — affichage unifié d'une date limite avec :
 *   - Préfixe "DDM" ou "DLC" selon `typeDateLimite` (default DDM)
 *   - Coloration d'alerte si la date est dépassée :
 *     - DLC dépassée → **rouge** (interdiction légale, R. 412-21 Code Conso)
 *     - DDM dépassée → **orange** (simple avertissement)
 *   - Icône `error` (DLC) ou `warning` (DDM) si dépassée
 *   - Tooltip explicatif au survol
 *
 * Usage :
 * ```html
 * <vl-date-limite-display [dluo]="lot.dluo" [typeDateLimite]="lot.typeDateLimite"/>
 * ```
 *
 * Variantes :
 *   - `size`: 'normal' (default) | 'compact' (caption-size, pour les cards denses)
 *   - `format`: format de date custom (default 'shortDate')
 *
 * Cf. `docs/plans/roadmap-ddm-prepa.md`.
 */
@Component({
  selector: "vl-date-limite-display",
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  template: `
    @if (dluo()) {
      <span
        class="date-limite-display"
        [style.background-color]="bgColor()"
        [style.color]="textColor()"
        [style.border]="borderStyle()"
        [style.font-weight]="isExpired() ? '600' : null"
        [style.font-size]="size() === 'compact' ? '0.85em' : null"
        [style.white-space]="'nowrap'"
        [matTooltip]="tooltipText()">
        @if (isExpired()) {
          <mat-icon
            class="date-limite-display__icon"
            [style.color]="textColor()">
            {{ isDlc() ? "error" : "warning" }}
          </mat-icon>
        }
        <strong [style.color]="textColor()" class="date-limite-display__label">
          {{ isDlc() ? "DLC" : "DDM" }}
        </strong>
        <span [style.color]="textColor()">{{ dluo() | date: format() }}</span>
      </span>
    } @else if (showEmpty()) {
      <span class="date-limite-display__empty">Pas de date limite</span>
    }
  `,
  styles: [
    `
      .date-limite-display {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 6px;
        border-radius: 4px;
        line-height: 1.2;
      }

      .date-limite-display__label {
        font-weight: 600;
        font-size: 0.85em;
      }

      .date-limite-display__icon {
        font-size: 16px;
        height: 16px;
        width: 16px;
      }

      .date-limite-display__empty {
        font-style: italic;
        opacity: 0.55;
        font-size: 0.9em;
      }
    `,
  ],
})
export class DateLimiteDisplayComponent {
  dluo = input<Date | string | null | undefined>();
  typeDateLimite = input<"DDM" | "DLC" | "AUCUNE" | null | undefined>();
  size = input<"normal" | "compact">("normal");
  format = input<string>("shortDate");
  /** Affiche "Pas de date limite" en italique léger si dluo est absent. Default true. */
  showEmpty = input<boolean>(true);

  isDlc = computed(() => this.typeDateLimite() === "DLC");

  isExpired = computed(() => {
    const d = this.dluo();
    if (!d) return false;
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  });

  tooltipText = computed(() => {
    if (!this.isExpired()) return "";
    return this.isDlc()
      ? "DLC dépassée — vente interdite (R. 412-21 Code Conso). Isoler et détruire."
      : "DDM dépassée — qualité dégradée mais consommation possible. Vérifier avant utilisation.";
  });

  bgColor = computed(() => {
    if (!this.isExpired()) return null;
    return this.isDlc() ? "#fde8ec" : "#fff4d6";
  });

  textColor = computed(() => {
    if (!this.isExpired()) return null;
    return this.isDlc() ? "#c0392b" : "#b07d00";
  });

  borderStyle = computed(() => {
    if (!this.isExpired()) return null;
    return this.isDlc() ? "1px solid #f5c2c7" : "1px solid #f0d77a";
  });
}
